<!-- ─── Layout ───────────────────────────────────────────────────────────── -->
<div class="row g-3">

    <!-- ─── Left panel: Tree ─────────────────────────────────────────────── -->
    <div class="col-12 col-md-6">
        <app-card>

            <!-- Card header -->
            <ng-template appTemplate="headerTemplate">
                <div class="card-header">
                    <div class="d-flex align-items-center justify-content-between w-100">
                        <h5 class="mb-0">{{ rootUnit()?.name ?? (TRANSLATE_KEY_MAP.TITLE() | translate) }}</h5>
                        <div class="d-flex gap-2">
                            <!-- Add root-level unit -->
                            <button
                                appButton
                                size="sm"
                                [iconOnly]="true"
                                variant="primary"
                                [title]="TRANSLATE_KEY_MAP.GENERAL.ADD_ROOT_UNIT() | translate"
                                [appTooltip]="TRANSLATE_KEY_MAP.GENERAL.ADD_ROOT_UNIT() | translate"
                                (click)="showAddUnitForm(rootUnit())"
                            >
                                <i class="ti ti-plus"></i>
                            </button>
                            <!-- Reload tree -->
                            <button
                                appButton
                                size="sm"
                                variant="secondary"
                                appearance="label"
                                [iconOnly]="true"
                                [title]="TRANSLATE_KEY_MAP.GENERAL.RELOAD() | translate"
                                [appTooltip]="TRANSLATE_KEY_MAP.GENERAL.RELOAD() | translate"
                                (click)="loadFirstLevel()"
                            >
                                <i class="ti ti-refresh"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </ng-template>

            <!-- Tree -->
            <app-tree
                [value]="treeNodes()"
                [loading]="isLoadingTree()"
                selectionMode="single"
                scrollHeight="500px"
                [(selection)]="selectedNode"
                (onLazyLoad)="onLazyLoad($event)"
                class="tree_units"
            >
                <!-- Custom node template: label + hover action buttons -->
                <ng-template appTemplate="nodeTemplate" let-node>
                    <div class="d-flex align-items-center justify-content-between w-100 unit-node-row">

                        <!-- Node label -->
                        <span class="unit-node-label">{{ node.label }}</span>

                        <!-- Hover actions -->
                        <div class="unit-node-actions d-flex gap-1 ms-2">
                            <!-- Add child -->
                            <button
                                appButton
                                size="sm"
                                variant="primary"
                                appearance="text"
                                [iconOnly]="true"
                                [rounded]="true"
                                [title]="TRANSLATE_KEY_MAP.FORM.ADD_UNIT() | translate"
                                [appTooltip]="TRANSLATE_KEY_MAP.FORM.ADD_UNIT() | translate"
                                appStopPropagation
                                (click)="showAddUnitForm(node.data)"
                            >
                                <i class="fa-solid fa-plus fa-lg"></i>
                            </button>

                            <!-- Edit -->
                            <button
                                appButton
                                size="sm"
                                variant="primary"
                                appearance="text"
                                [iconOnly]="true"
                                [rounded]="true"
                                [title]="TRANSLATE_KEY_MAP.FORM.EDIT_UNIT() | translate"
                                [appTooltip]="TRANSLATE_KEY_MAP.FORM.EDIT_UNIT() | translate"
                                appStopPropagation
                                (click)="showEditForm(node.data)"
                            >
                                <i class="fa-solid fa-pencil fa-lg"></i>
                            </button>

                            <!-- Delete -->
                            <button
                                appButton
                                size="sm"
                                variant="primary"
                                appearance="text"
                                [iconOnly]="true"
                                [rounded]="true"
                                [title]="TRANSLATE_KEY_MAP.FORM.DELETE_UNIT() | translate"
                                [appTooltip]="TRANSLATE_KEY_MAP.FORM.DELETE_UNIT() | translate"
                                appStopPropagation
                                (click)="confirmDelete(node.data)"
                            >
                                <i class="fa-solid fa-trash fa-lg"></i>
                            </button>


                        </div>
                    </div>
                </ng-template>
            </app-tree>

        </app-card>
    </div>

    <!-- ─── Right panel: Details + Map ───────────────────────────────────── -->
    <div class="col-12 col-md-6">

        <app-unit-details
            [loading]="isLoadingTree()"
            [unit]="selectedUnitDetails()"
        />

    </div>

</div>

<!-- ─── Add / Edit form modal ─────────────────────────────────────────────── -->
<app-modal
    identifier="unitFormModal"
    size="lg"
    [scrollable]="true"
    [(visible)]="formModalVisible"
>
    <!-- start header -->
    <ng-template appTemplate="headerTemplate">
        <h5 class="modal-title">
            @let title = unitFormApi.isCreationMode() ? TRANSLATE_KEY_MAP.FORM.ADD_UNIT() : TRANSLATE_KEY_MAP.FORM.EDIT_UNIT();
            {{ title | translate }}
        </h5>
    </ng-template>

    <!-- start unit-form -->
    <app-unit-form modalBody #unitFormApi="unitFormApi" (submitted)="onFormSubmitted($event)" />

    <!-- start footer -->
    <ng-template appTemplate="footerTemplate">
        <button appButton variant="secondary" appearance="label" (click)="closeFormModal()">
            {{ 'GENERAL.CANCEL' | translate }}
        </button>
        <button appButton variant="primary" [disabled]="unitFormApi.frazzled" (click)="formRef().submit()">
            @if (unitFormApi.isLoading()) {
                <app-spin-kit-wave style="--sk-size: 20px;" bgColor="var(--bs-white)" />
            } @else {
                {{ (unitFormApi.isCreationMode() ? 'GENERAL.SAVE': 'GENERAL.UPDATE') | translate }}
            }
        </button>
    </ng-template>

</app-modal>

<!-- ─── Delete confirmation modal ─────────────────────────────────────────── -->
<app-modal
    identifier="unitDeleteModal"
    [(visible)]="deleteModalVisible"
    [centered]="true"
    [cornerButton]="false"
    [backdrop]="isLoadingAction() ? 'static' : true"
    [title]="TRANSLATE_KEY_MAP.FORM.DELETE_CONFIRM() | translate"
>

    <ng-template appTemplate="bodyTemplate">
        
        @if (pendingDeleteUnit) {
            <div appListGroup>
                <div appListGroupItem class="d-flex justify-content-between align-items-start">
                <div class="d-flex align-items-start flex-column">
                    <h6 class="mb-1">{{pendingDeleteUnit?.name}}</h6>
                    <small>{{pendingDeleteUnit.description}}</small>
                </div>
                @let hasChildrenBadge = pendingDeleteUnit?.hasChildrenBadge;

                @if(hasChildrenBadge != null) {
                    <span [appBadge]="hasChildrenBadge.variant" [appearance]="hasChildrenBadge.appearance">
                        {{ hasChildrenBadge.label | translate }}
                    </span>
                }
                </div>
            </div>
        }
    </ng-template>

    <ng-template appTemplate="footerTemplate">
        <div class="d-flex justify-content-end flex-wrap flex-column-reverse flex-sm-row gap-3 w-100">
            <button
                appButton
                appWaves
                variant="secondary"
                appearance="label"
                class="flex-grow-1 flex-sm-grow-0"
                [disabled]="isLoadingAction()"
                (click)="deleteModalVisible.set(false)"
            >
                {{ 'GENERAL.CANCEL' | translate }}
            </button>
            <button
                appButton
                appWaves
                variant="danger"
                class="flex-grow-1"
                [disabled]="isLoadingAction()"
                (click)="onDeleteConfirmed()"
            >
                @if (isLoadingAction()) {
                    <app-spin-kit-wave style="--sk-size: 20px;" bgColor="var(--bs-white)" />
                } @else {
                    {{ 'GENERAL.DELETE' | translate }}
                }
            </button>
        </div>
    </ng-template>

</app-modal>
